// Left-rail view switcher — Frontend (/studio), Infra (/infra), Agents
// (/team-and-agents). Replaces the header "Infra" tab; shown on BOTH /studio and
// /infra so you can switch between them from either side. Lives in shared so the
// canvas + infra packages can both mount it. (2026-06-18 per Nika "add a sidebar
// section … Frontend / Infra / Agents … switched from the left sidebar … on both".)

// Exported so the AppNavbar More dropdown can mirror the same four canvas
// views (single source of truth — the navbar VIEWS section maps these into its
// own item shape rather than re-typing the routes).
import { stripWorkspacePrefix } from '../functions/workspacePrefix.js'
import { navItemVisible } from '../functions/featureFlags.js'

// Tasks removed from the switcher (2026-07-25 per Nika "remove tasks from
// views") — /tickets is reached via the header apps launcher / direct URL;
// its rail is filters-only now, so it no longer participates in VIEWS.
// Every consumer maps this array (AppNavbar VIEWS section, searchApps
// launcher tiles), so the entry disappears everywhere from this one source.
//
// THE APP SPLIT (docs/APP_SPLIT.md, 2026-10-19 per Nika "strictly divide
// workspace apps from external apps … they should not go to the same
// canvas"): the old single `apps → /studio` lens is two views now —
// Workspace apps (/apps: the add-ons installed in and built for this
// workspace) and Websites (/studio: the board of projects that are deployed
// and published). The navbar "Apps" entry still opens /studio; this rail is
// where the two kinds part.
export const VIEW_NAV = [
  { key: 'workspaceApps', label: 'shell.viewNav.workspaceApps', icon: 'grid', path: '/apps' },
  { key: 'websites', label: 'shell.viewNav.websites', icon: 'globe', path: '/studio' },
  { key: 'infra', label: 'shell.viewNav.infra', icon: 'server', path: '/infra' },
  { key: 'agents', label: 'shell.viewNav.agents', icon: 'people', path: '/agents' }
]

// Current route, query-stripped. The `/w/<org-slug>` prefix is stripped so the
// intranet view routes (/infra, /tickets, /agents, …) still resolve under
// multi-workspace tabs (§6d) — no-op until Phase 3 emits the prefix.
// Kept at module scope — still used by `isViewNavRoute` below, which is a
// plain exported function (not a DOMQL prop), so it's never itself
// frank-serialized and this closure resolves fine for its own callers.
const _canvasViewNavRoutePath = (s, el) =>
  stripWorkspacePrefix(
    (
      s.root?.[el?.call?.('slotPaneKeyForStrict', el)] ||
      s.root?.currentPath ||
      (typeof window !== 'undefined' ? window.location.pathname : '') ||
      ''
    ).split('?')[0]
  )

// Every route the switcher CONTAINS — the VIEW_NAV paths plus their aliases:
// /canvas mounts the same dashboard surface as /studio (canvas/pages/index.js),
// the agents canvas answers on three routes. (/tickets routes left with the
// Tasks entry — the switcher no longer renders on the tickets board.)
const VIEW_ROUTES = [...VIEW_NAV.map((d) => d.path), '/canvas', '/team-and-agents', '/my-agents']

// Membership gate for the shared AppSidebar's global ViewSwitcher card: the
// VIEWS nav renders ONLY on the pages it links to — on any other AppSidebar
// rail (calendar, resolutions, feed, preview, …) the card hides itself,
// so non-view rails don't each need a hand-written `ViewSwitcher: null`.
// (2026-07-24 per Nika "ViewSwitcher in sidebar should be only visible for
// pages that it contains".)
// NOT a DOMQL prop — a plain exported function AppSidebar.js imports and
// calls from its own `hide` factory. That cross-file import is the
// deliberately out-of-scope frank concern (see hard rule 3 in the sweep
// ticket); its _canvasViewNavRoutePath/VIEW_ROUTES closure here is ordinary JS and
// resolves fine for every real (non-serialized) caller of this function.
export const isViewNavRoute = (s, el) => {
  const p = _canvasViewNavRoutePath(s, el)
  return VIEW_ROUTES.some((base) => p === base || p.indexOf(base + '/') === 0)
}
// The rail row law lives on AppSidebarRow (brief §4.3 item 3): line icon,
// label, the active look (`selected` + `title` + 700). This row only says
// WHEN it is active and where it goes. It extends the SHARED row — the
// shell-only `SidebarRow` never resolved in the standalone canvas app.
export const CanvasViewNavRow = {
  extends: 'AppSidebarRow',
  // FA204 / frank trap — currentView (+ its routePath dependency) used to be
  // bare module-scope functions read from inside `isActive`/`onClick`
  // factories; they read as undefined once this component is frank.toJSON'd
  // for platform delivery (workspace d6ea1142,
  // project_frank_drops_module_local_helpers). Moved into this component's
  // own scope, called via `el.scope.currentView` — duplicated (not shared)
  // in CanvasViewNavGroup below since `el.scope` only resolves within the
  // tree of the SPECIFIC top-level export it's defined on.
  scope: {
    routePath(s, el) {
      const paneKey = this.canvasViewPaneKey || el?.call?.('slotPaneKeyForStrict', el)
      return stripWorkspacePrefix(
        (
          s.root?.[paneKey] ||
          s.root?.currentPath ||
          (typeof window !== 'undefined' ? window.location.pathname : '') ||
          ''
        ).split('?')[0]
      )
    },
    // Which view the current route belongs to (drives the active row). Each
    // view is its own page, so this is resolved fresh on mount — switching
    // navigates.
    currentView(s, el) {
      const p = this.routePath(s, el)
      if (p.indexOf('/infra') === 0) return 'infra'
      if (
        p.indexOf('/agents') === 0 ||
        p.indexOf('/team-and-agents') === 0 ||
        p.indexOf('/my-agents') === 0
      )
        return 'agents'
      // The app split's two views (VIEW_NAV): /apps is Workspace apps; the
      // board (/studio, /canvas) is Websites.
      if (p === '/apps' || p.indexOf('/apps/') === 0) return 'workspaceApps'
      return 'websites' // /studio (+ default)
    }
  },
  isActive: (el, s) => el.scope.currentView(s, el) === s.key,
  onClick: (e, el, s) => {
    e.preventDefault()
    el.call('closeDropdown', 'canvasViews')
    if (el.scope.currentView(s, el) === s.key && !e.metaKey && !e.ctrlKey) return
    // Use the shell's one navigation policy: tenancy, pane intent and pins.
    if (typeof el.context?.functions?.openAppPage === 'function') {
      el.call('openAppPage', s.path, { preferSlot: true, split: e.metaKey || e.ctrlKey })
      return
    }
    el.router(s.path, el.getRoot())
  }
}

// The section — drop into any sidebar (e.g. InfraSidebar) above its own groups.
export const CanvasViewNavGroup = {
  // The shared group (its eyebrow law, and it resolves in the standalone
  // canvas app, which never loads the tickets package).
  extends: 'AppSidebarGroup',
  // FA204 / frank trap — duplicate of CanvasViewNavRow's scope above (el.scope
  // doesn't cross top-level export boundaries), plus VIEW_NAV/viewNavChildren
  // which only this export's `children` factory needs. VIEW_NAV itself stays
  // exported at module scope too (AppNavbar's More dropdown imports it
  // cross-file — a separate, deliberately out-of-scope concern for this fix).
  scope: {
    // MUST equal the exported VIEW_NAV above (CanvasViewNav.test.js holds the
    // two equal) — this copy is what the rail renders; the app split changed
    // only the export and the rail kept "Apps · Infra · Agents" on dev
    // (check 5, 2026-10-21).
    VIEW_NAV: [
      { key: 'workspaceApps', label: 'shell.viewNav.workspaceApps', icon: 'grid', path: '/apps' },
      { key: 'websites', label: 'shell.viewNav.websites', icon: 'globe', path: '/studio' },
      { key: 'infra', label: 'shell.viewNav.infra', icon: 'server', path: '/infra' },
      { key: 'agents', label: 'shell.viewNav.agents', icon: 'people', path: '/agents' }
    ],
    routePath(s, el) {
      const paneKey = this.canvasViewPaneKey || el?.call?.('slotPaneKeyForStrict', el)
      return stripWorkspacePrefix(
        (
          s.root?.[paneKey] ||
          s.root?.currentPath ||
          (typeof window !== 'undefined' ? window.location.pathname : '') ||
          ''
        ).split('?')[0]
      )
    },
    currentView(s, el) {
      const p = this.routePath(s, el)
      if (p.indexOf('/infra') === 0) return 'infra'
      if (
        p.indexOf('/agents') === 0 ||
        p.indexOf('/team-and-agents') === 0 ||
        p.indexOf('/my-agents') === 0
      )
        return 'agents'
      // The app split's two views (VIEW_NAV): /apps is Workspace apps; the
      // board (/studio, /canvas) is Websites.
      if (p === '/apps' || p.indexOf('/apps/') === 0) return 'workspaceApps'
      return 'websites' // /studio (+ default)
    },
    // Feature-flag gate: a view toggled off at /flags drops out of the
    // switcher (navItemVisible maps each path to its flag — studio/infra/
    // tasks/agents).
    viewNavChildren(el, s) {
      return this.VIEW_NAV.filter((d) => navItemVisible(s.root, d)).map((d) => ({
        ...d,
        _v: this.currentView(s, el)
      }))
    }
  },
  label: 'shell.viewNav.groupLabel',
  // No bottom margin — the VIEWS nav is the only content of its card, so the
  // inherited TicketsSidebarGroup marginBottom just left dead space at the card
  // foot. (2026-06-19 per Nika "remove margin bottom from ViewSwitcher > ViewNav".)
  marginBottom: '0',
  gap: 'Y',
  Header: { padding: '0 Z1' },
  AppCapsuleBtnToolbar: {
    gap: 'Z1',
    width: '100%',
    justifyContent: 'flex-start',
    minWidth: '0',
    theme: 'transparent',
    padding: 'Y Z1',
    round: 'radiusControl',
    subscribeTo: ['currentPath', '_canvasSlot', '_canvasSlotSecond', '_featureFlags', 'workspace'],
    'aria-haspopup': 'menu',
    'aria-label': (el) => el.call('polyglot', 'shell.viewNav.switchView'),
    Icon: {
      name: (el, s) =>
        el.scope.VIEW_NAV.find((v) => v.key === el.scope.currentView(s, el))?.icon || 'layers'
    },
    Text: {
      flex: '1',
      textAlign: 'left',
      text: (el, s) =>
        el.call(
          'polyglot',
          el.scope.VIEW_NAV.find((v) => v.key === el.scope.currentView(s, el))?.label ||
            'shell.app.studio'
        )
    },
    Icon_1: { name: 'chevronDown', boxSize: 'iconSm' },
    onClick: (e, el) => {
      e.preventDefault()
      e.stopPropagation()
      el.call('openDropdown', {
        id: 'canvasViews',
        anchorEl: el,
        placement: 'bottom-start',
        content: {
          extends: 'CanvasViewMenu',
          scope: { canvasViewPaneKey: el.call('slotPaneKeyForStrict', el) }
        }
      })
    }
  },
  List: {
    hide: true,
    flow: 'y',
    gap: 'V',
    subscribeTo: ['currentPath', '_canvasSlot', '_canvasSlotSecond', '_featureFlags', 'workspace'],
    childExtends: 'CanvasViewNavRow',
    children: (el, s) => el.scope.viewNavChildren(el, s),
    childrenAs: 'state'
  }
}

// Standalone left rail (for /studio, which has no persistent sidebar). Same fixed
// glass-card shell as InfraSidebar, holding only the view nav.
export const CanvasViewRail = {
  // A fixed COLUMN of glass cards (not a single card) so /agents can mount its
  // AGENTS filter card below the VIEWS card as a separate sibling. The card
  // chrome moved from the rail onto ViewsCard. (2026-06-19 per Nika "split the
  // /agents sidebar into 2 different cards".) Each card is the sidebar card
  // (AppSidebar.js AppSidebarCard, Nika 2026-10-30 "A: navbar glass").
  position: 'fixed',
  // Clear of a drawer the assistant docks on the left (the shell's
  // `--assistant-inset-left`, zero when nothing is docked there or outside
  // the shell — the token-only zero fallback the page rails use).
  left: 'calc(var(--assistant-inset-left, calc(var(--spacing-A) * 0)) + var(--spacing-X2))',
  // --shell-top (registered @property in app.js, so it resolves to the SAME px
  // here as on the app content pad / PageSlot / every other canvas rail) — was
  // a stale hardcoded 'D1' that left this rail ~16px lower than the shared
  // inset. Bare custom-property name so scratch wraps it in var() once (a
  // 'var(...)'/calc value compiles to an empty atomic rule). No bottom anchor:
  // the rail keeps its natural height so the canvas below the cards stays
  // pannable — a full-height container would eat those pointer events.
  // (2026-07-03 per Nika "same layout and positioning" across /studio /infra
  // /tickets /agents.)
  top: '--shell-top',
  width: 'G1',
  minWidth: 'G1',
  maxWidth: 'G1',
  zIndex: '1200',
  flow: 'y',
  gap: 'Z',
  fontSize: 'Z',
  pointerEvents: 'auto',
  childExtends: 'AppSidebarCard',

  ViewsCard: {
    flexShrink: '0',
    ViewNav: { extends: 'CanvasViewNavGroup' }
  }
}

// One compact lens in every canvas rail; the menu exposes the same registry.
export const CanvasViewMenu = {
  extends: ['MenuPanel', 'CanvasViewNavGroup'],
  // The portal keeps the pane that opened it, so its labels and navigation
  // follow that pane rather than the background canvas.
  onCreate: (el) => {
    if (el.scope.canvasViewPaneKey) el._slotPaneKey = el.scope.canvasViewPaneKey
  },
  Header: null,
  AppCapsuleBtnToolbar: null,
  minWidth: 'G1',
  padding: 'Z',
  List: {
    hide: false,
    childProps: {
      role: 'menuitemradio',
      'aria-checked': (el, s) => String(el.scope.currentView(s, el) === s.key)
    }
  }
}

// Canvas controls use the same phone drawer as the other workspace apps.
// The phone dock owns its opener and closes it on navigation.
export const CanvasControlsSheet = {
  extends: 'MobileRailSheet',
  stateKey: '_canvasRailOpen',
  railLabel: (el) => el.call('polyglot', 'shell.phoneNav.pageMenu'),
  Card: {
    Content: {
      flow: 'y',
      gap: 'A',
      CanvasViewNavGroup: {}
    }
  }
}
